/**
 * Answer-engine (AEO) helpers.
 *
 * Builds a single, self-contained sentence that answers the page's core
 * question without needing any surrounding context. Used for the on-page
 * "Quick answer" block, the meta description, and speakable markup so
 * assistants and AI search engines can lift a correct, attributable answer.
 */
import type { DataTypeKey } from "@/lib/vehicle-data";
import { buildMaintenanceProfile } from "@/lib/maintenance";

/**
 * Machine values arrive at full float precision (191.339 in). Snippets read as
 * scraped data when that lands in a title or meta description, so every
 * measurement is rendered to at most one decimal.
 */
export function n1(v: unknown): string {
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return String(v ?? "");
  return (Math.round(n * 10) / 10).toLocaleString(undefined, { maximumFractionDigits: 1 });
}

export interface AnswerInput {
  fullName: string;
  latestYear: number | null;
  vehicles: any[];
  specs: any[];
  dimensions?: any[];
  safety?: any[];
  makeSlug?: string;
  makeName?: string;
}

function uniq(rows: any[], key: string): string[] {
  return Array.from(new Set(rows.map((r) => r?.[key]).filter((v): v is string => Boolean(v))));
} /**
 * Rows for the page's model year when known, so a sentence about the latest
 * model year never reports a record set by an older model year. Mirrors how
 * the spec titles scope their labels to the latest year (see mpgLabel).
 */
function forYear(rows: any[], latestYear: number | null): any[] {
  if (!latestYear) return rows;
  const scoped = rows.filter((r) => r?.year === latestYear);
  return scoped.length ? scoped : rows;
}

export function buildQuickAnswer(dataType: DataTypeKey, input: AnswerInput): string {
  const { fullName, latestYear, vehicles, specs } = input;
  const yearLabel = latestYear ? `${latestYear} ` : "";
  const s = specs[0];

  switch (dataType) {
    case "mpg": {
      const rows = forYear(vehicles, latestYear);
      const best = rows.reduce((b, v) => (v.mpg_combined && (!b || v.mpg_combined > b.mpg_combined) ? v : b), rows[0]);
      if (!best?.mpg_combined) break;
      return `The ${yearLabel}${fullName} gets ${best.mpg_combined} MPG combined (${best.mpg_city ?? "—"} city / ${best.mpg_hwy ?? "—"} highway) in EPA testing.`;
    }
    case "engine": {
      const cyl = uniq(vehicles, "cylinders");
      const disp = Array.from(new Set(vehicles.map((v) => v.displacement).filter(Boolean))).sort(
        (a: any, b: any) => a - b,
      );
      if (!cyl.length && !disp.length) break;
      return `The ${yearLabel}${fullName} is offered with ${disp.length ? `${disp[0]}L${disp.length > 1 ? `–${disp[disp.length - 1]}L` : ""} ` : ""}engines${cyl.length ? ` in ${cyl.join(" and ")}-cylinder form` : ""}, per EPA fuel-economy records.`;
    }
    case "fuel-cost": {
      const rows = forYear(vehicles, latestYear);
      const cheapest = rows.reduce(
        (b, v) => (v.annual_fuel_cost && (!b || v.annual_fuel_cost < b.annual_fuel_cost) ? v : b),
        rows[0],
      );
      if (!cheapest?.annual_fuel_cost) break;
      return `Running a ${yearLabel}${fullName} costs about $${cheapest.annual_fuel_cost.toLocaleString()} per year in fuel, based on 15,000 miles of driving at average U.S. fuel prices.`;
    }
    case "transmission": {
      const list = uniq(vehicles, "transmission");
      if (!list.length) break;
      return `The ${yearLabel}${fullName} uses ${list.slice(0, 3).join(", ")}${list.length > 3 ? " and other" : ""} transmission${list.length > 1 ? "s" : ""}, depending on engine and trim.`;
    }
    case "drivetrain": {
      const list = uniq(vehicles, "drive");
      if (!list.length) break;
      return `The ${yearLabel}${fullName} is available in ${list.slice(0, 3).join(", ")}${list.length > 3 ? " and other" : ""} drivetrain configuration${list.length > 1 ? "s" : ""}.`;
    }
    case "vehicle-class": {
      const list = uniq(vehicles, "vehicle_class");
      if (!list.length) break;
      return `The EPA classifies the ${yearLabel}${fullName} as a ${list.join(" / ")}, a rating based on body style and interior volume.`;
    }
    case "maintenance-schedule": {
      if (!vehicles.length) break;
      const towingLbs = specs.reduce<number | null>(
        (b, r) => (r?.towing_capacity_lbs && (!b || r.towing_capacity_lbs > b) ? r.towing_capacity_lbs : b),
        null,
      );
      const curbWeightLbs = (input.dimensions ?? []).reduce<number | null>(
        (b, d) => (d?.curb_weight_lbs && (!b || d.curb_weight_lbs > b) ? Math.round(d.curb_weight_lbs) : b),
        null,
      );
      const p = buildMaintenanceProfile(vehicles, {
        makeSlug: input.makeSlug ?? null,
        makeName: input.makeName ?? null,
        towingLbs,
        curbWeightLbs,
      });
      const list = p.items
        // The oil interval is stated once in the lead clause below; repeating
        // the oil item inside the supporting list reads as duplication.
        .filter((i) => i.intervalMiles > 0 && !/oil/i.test(i.service))
        .sort((a, b) => a.intervalMiles - b.intervalMiles)
        .slice(0, 5)
        .map((i) => `${i.service.toLowerCase()} every ${i.intervalMiles.toLocaleString()} miles`)
        .join(", ");
      if (p.isElectric) {
        return `The ${yearLabel}${fullName} needs no oil changes; its main service items are ${list}, plus brake fluid every 3 years.`;
      }
      return `The ${yearLabel}${fullName} needs an oil and filter change every ${p.oilIntervalMiles.toLocaleString()} miles or 12 months, with ${list} making up the rest of its schedule.`;
    }
    case "towing-capacity": {
      const rows = forYear(specs, latestYear);
      const max = rows.reduce(
        (b: any, r: any) => ((r.towing_capacity_lbs ?? 0) > (b?.towing_capacity_lbs ?? 0) ? r : b),
        null as any,
      );
      if (!max?.towing_capacity_lbs) break;
      return `The ${yearLabel}${fullName} can tow up to ${max.towing_capacity_lbs.toLocaleString()} lbs when properly equipped${max.trim ? ` (${max.trim})` : ""}; capacity drops with lighter axle ratios, smaller engines, and heavier cab or bed configurations.`;
    }
    case "tire-size": {
      if (!s?.front_tire_size) break;
      return `The ${yearLabel}${fullName} uses ${s.front_tire_size} tires${s.rear_tire_size && s.rear_tire_size !== s.front_tire_size ? ` up front and ${s.rear_tire_size} at the rear` : ""}${s.wheel_size ? ` on ${s.wheel_size} wheels` : ""}${s.tire_pressure_front_psi ? `, inflated to ${s.tire_pressure_front_psi} PSI cold` : ""}.`;
    }
    case "bolt-pattern": {
      if (!s?.bolt_pattern) break;
      return `The ${yearLabel}${fullName} has a ${s.bolt_pattern} bolt pattern${s.center_bore_mm ? ` with a ${n1(s.center_bore_mm)} mm center bore` : ""}${s.lug_nut_torque_ftlb ? `, torqued to ${n1(s.lug_nut_torque_ftlb)} ft-lb` : ""}.`;
    }
    case "bed-dimensions": {
      const beds = forYear(specs, latestYear).filter((r: any) => r.bed_length_in);
      if (!beds.length) break;
      const longest = beds.reduce((b: any, r: any) => (r.bed_length_in > b.bed_length_in ? r : b));
      return `The ${yearLabel}${fullName} bed is ${n1(longest.bed_length_in)} in (${(longest.bed_length_in / 12).toFixed(1)} ft) long${longest.bed_config ? ` in the ${longest.bed_config}` : ""}${longest.bed_width_between_wheelwells_in ? `, with ${n1(longest.bed_width_between_wheelwells_in)} in between the wheel wells` : ""}${longest.bed_volume_cuft ? ` and ${n1(longest.bed_volume_cuft)} cu ft of cargo volume` : ""}.`;
    }
    case "fuel-tank-capacity": {
      const t = specs.find((r: any) => r.fuel_tank_gal);
      if (!t) break;
      const mpgVals = vehicles.filter((v: any) => v.year === t.year && v.mpg_combined).map((v: any) => v.mpg_combined);
      const mpg = mpgVals.length
        ? Math.round(mpgVals.reduce((a: number, b: number) => a + b, 0) / mpgVals.length)
        : null;
      return `The ${yearLabel}${fullName} has a ${n1(t.fuel_tank_gal)}-gallon fuel tank${t.fuel_tank_gal_optional ? ` (${n1(t.fuel_tank_gal_optional)} gallons with the optional extended-range tank)` : ""}${mpg ? `, good for roughly ${Math.round(t.fuel_tank_gal * mpg).toLocaleString()} miles of range at ${mpg} MPG combined` : ""}.`;
    }
    case "curb-weight": {
      const rows = forYear(input.dimensions ?? [], latestYear).filter((d: any) => d.curb_weight_lbs);
      if (!rows.length) break;
      const lo = Math.round(Math.min(...rows.map((d: any) => d.curb_weight_lbs)));
      const hi = Math.round(Math.max(...rows.map((d: any) => d.curb_weight_lbs)));
      return `The ${yearLabel}${fullName} has a curb weight of ${lo.toLocaleString()}${hi !== lo ? ` to ${hi.toLocaleString()}` : ""} lbs depending on body style and drivetrain — that is the weight with fluids but no passengers or cargo.`;
    }
    case "dimensions": {
      const d = (input.dimensions ?? [])[0];
      if (!d?.length_in && !d?.wheelbase_in) break;
      const parts: string[] = [];
      if (d.length_in) parts.push(`${n1(d.length_in)} in long`);
      if (d.width_in) parts.push(`${n1(d.width_in)} in wide`);
      if (d.height_in) parts.push(`${n1(d.height_in)} in tall`);
      return `The ${d.year ?? latestYear ?? ""} ${fullName} measures ${parts.join(", ")}${d.wheelbase_in ? `, on a ${n1(d.wheelbase_in)} in wheelbase` : ""}${d.curb_weight_lbs ? `, with a curb weight of about ${Math.round(d.curb_weight_lbs).toLocaleString()} lbs` : ""}.`;
    }
    case "safety-ratings": {
      const r = (input.safety ?? []).find((x: any) => x.overall_rating) ?? (input.safety ?? [])[0];
      if (!r) break;
      return `NHTSA gives the ${r.year ?? latestYear ?? ""} ${fullName} an overall safety rating of ${r.overall_rating ?? "—"} out of 5 stars${r.overall_front_rating ? `, with ${r.overall_front_rating} stars in the frontal crash test` : ""}${r.rollover_rating ? ` and ${r.rollover_rating} stars for rollover resistance` : ""}.`;
    }
    case "ev-range": {
      const rows = forYear(vehicles, latestYear);
      const best = rows.reduce(
        (b: any, v: any) => (v.ev_range_miles && (!b || v.ev_range_miles > b.ev_range_miles) ? v : b),
        null as any,
      );
      if (!best?.ev_range_miles) break;
      return `The ${yearLabel}${fullName} is EPA-rated for up to ${best.ev_range_miles} miles of electric range${best.charge_240v_hours ? `, and recharges in about ${best.charge_240v_hours} hours on a 240-volt Level 2 charger` : ""}.`;
    }
  }

  return "";
}
